// Visual tokens for the Bulk Actions wizard. Same surfaces, radii and blur as
// EmployeeActions.styles.js so the wizard reads as part of the Changes page.

export const STATUS_COLORS = {
  valid: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444",
};

export const OUTCOME_COLORS = {
  completed: "#10b981",
  scheduled: "#3b82f6",
  pending_approval: "#f59e0b",
  failed: "#ef4444",
  skipped: "#94a3b8",
  queued: "#94a3b8",
  processing: "#6366f1",
  created: "#10b981",
};

export const createBulkTokens = (theme) => {
  const dark = theme === "dark";
  return {
    dark,
    text: dark ? "#f1f5f9" : "#0f172a",
    muted: dark ? "#94a3b8" : "#64748b",
    faint: dark ? "rgba(255,255,255,0.45)" : "rgba(15,23,42,0.45)",
    border: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.09)",
    borderStrong: dark ? "rgba(255,255,255,0.22)" : "rgba(15,23,42,0.16)",
    subtle: dark ? "rgba(255,255,255,0.04)" : "#f8fafc",
    subtle2: dark ? "rgba(255,255,255,0.07)" : "#f1f5f9",
    surface: dark
      ? "linear-gradient(135deg, rgba(30, 41, 59, 0.99), rgba(51, 65, 85, 0.99))"
      : "linear-gradient(135deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 252, 0.99))",
    surfaceSolid: dark ? "#1e293b" : "#ffffff",
    accent: "#2e7cba",
    accentGradient: "#2e7cba",
    accentSoft: dark ? "rgba(46,124,186,0.18)" : "rgba(46,124,186,0.09)",
    shadow: "none",
  };
};

export const createBulkStyles = (theme, isRTL) => {
  const k = createBulkTokens(theme);
  const btnBase = {
    height: 42,
    padding: "0 18px",
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "transform .15s ease, background .2s ease, opacity .2s ease",
    fontFamily: "inherit",
  };
  return {
    k,
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(2, 6, 23, 0.72)",
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1100,
      padding: "clamp(12px, 3vw, 24px)",
      animation: "nxbFade .25s ease",
    },
    shell: {
      width: "min(1120px, 100%)",
      height: "min(860px, 94vh)",
      background: k.surface,
      color: k.text,
      borderRadius: 24,
      border: `1px solid ${k.border}`,
      boxShadow: k.shadow,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      direction: isRTL ? "rtl" : "ltr",
      animation: "nxbRise .35s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    header: {
      padding: "22px 28px 18px",
      borderBottom: `1px solid ${k.border}`,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
    },
    headerIcon: {
      width: 48,
      height: 48,
      borderRadius: 14,
      background: k.accentGradient,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      flexShrink: 0,
      boxShadow: "none",
    },
    title: { margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" },
    subtitle: { margin: "4px 0 0", fontSize: 14, color: k.muted, lineHeight: 1.5 },
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: 12,
      border: `1px solid ${k.border}`,
      background: k.subtle,
      color: k.text,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
    },
    stepper: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "14px 28px",
      borderBottom: `1px solid ${k.border}`,
      background: k.subtle,
      overflowX: "auto",
    },
    stepItem: (state) => ({
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: isRTL ? "6px 6px 6px 12px" : "6px 12px 6px 6px",
      borderRadius: 999,
      background: state === "active" ? k.accentSoft : "transparent",
      color: state === "todo" ? k.muted : k.text,
      fontWeight: state === "active" ? 700 : 600,
      fontSize: 13,
      whiteSpace: "nowrap",
    }),
    stepDot: (state) => ({
      width: 26,
      height: 26,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 800,
      background: state === "done" ? "#10b981" : state === "active" ? k.accentGradient : k.subtle2,
      color: state === "todo" ? k.muted : "#fff",
      border: state === "todo" ? `1px solid ${k.border}` : "none",
      flexShrink: 0,
    }),
    stepLine: (done) => ({
      flex: "1 1 24px",
      minWidth: 16,
      height: 2,
      borderRadius: 2,
      background: done ? "#10b981" : k.border,
    }),
    body: { flex: 1, overflowY: "auto", padding: "24px 28px" },
    footer: {
      padding: "16px 28px",
      borderTop: `1px solid ${k.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      background: k.subtle,
      flexWrap: "wrap",
    },
    footerGroup: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },

    // Buttons (SaaS: primary solid gradient, secondary outline, tertiary text)
    btnPrimary: {
      ...btnBase,
      border: "none",
      color: "#fff",
      background: k.accentGradient,
      boxShadow: "none",
    },
    btnSecondary: {
      ...btnBase,
      border: `1px solid ${k.borderStrong}`,
      color: k.text,
      background: k.dark ? "rgba(255,255,255,0.06)" : "#ffffff",
    },
    btnTertiary: {
      ...btnBase,
      border: "none",
      color: k.muted,
      background: "transparent",
      padding: "0 12px",
    },
    btnDanger: {
      ...btnBase,
      border: "none",
      color: "#fff",
      background: "linear-gradient(135deg, #ef4444, #dc2626)",
      boxShadow: "none",
    },
    btnDisabled: { opacity: 0.5, cursor: "not-allowed", boxShadow: "none" },

    sectionTitle: { margin: "0 0 4px", fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" },
    sectionHint: { margin: "0 0 20px", fontSize: 14, color: k.muted, lineHeight: 1.55 },

    // Step 1 — action cards
    cardGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
      gap: 16,
      marginBottom: 24,
    },
    actionCard: (selected, disabled, color) => ({
      position: "relative",
      textAlign: isRTL ? "right" : "left",
      padding: 20,
      borderRadius: 18,
      border: selected ? "2px solid transparent" : `1px solid ${k.border}`,
      background: selected
        ? `linear-gradient(${k.dark ? "#1e293b" : "#fff"}, ${k.dark ? "#1e293b" : "#fff"}) padding-box, ${color} border-box`
        : k.subtle,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      color: k.text,
      transition: "transform .18s ease",
      boxShadow: "none",
      fontFamily: "inherit",
      width: "100%",
    }),
    actionIcon: (color) => ({
      width: 44,
      height: 44,
      borderRadius: 13,
      background: color,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 14,
    }),
    check: (selected) => ({
      position: "absolute",
      top: 16,
      [isRTL ? "left" : "right"]: 16,
      width: 24,
      height: 24,
      borderRadius: 8,
      border: selected ? "none" : `1.5px solid ${k.borderStrong}`,
      background: selected ? k.accentGradient : "transparent",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    cardTitle: { margin: "0 0 6px", fontSize: 16, fontWeight: 800 },
    cardDesc: { margin: 0, fontSize: 13, color: k.muted, lineHeight: 1.55 },

    templatePanel: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
      padding: 18,
      borderRadius: 16,
      border: `1px dashed ${k.borderStrong}`,
      background: k.subtle,
    },
    segmented: {
      display: "inline-flex",
      padding: 3,
      borderRadius: 10,
      background: k.subtle2,
      border: `1px solid ${k.border}`,
      gap: 2,
    },
    segment: (active) => ({
      border: "none",
      padding: "6px 12px",
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer",
      background: active ? (k.dark ? "#334155" : "#fff") : "transparent",
      color: active ? k.text : k.muted,
      boxShadow: "none",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    }),

    // Step 2 — dropzone
    dropzone: (active) => ({
      border: `2px dashed ${active ? k.accent : k.borderStrong}`,
      background: active ? k.accentSoft : k.subtle,
      borderRadius: 20,
      padding: "48px 24px",
      textAlign: "center",
      transition: "all .2s ease",
      cursor: "pointer",
    }),
    dropIcon: {
      width: 64,
      height: 64,
      borderRadius: 18,
      margin: "0 auto 16px",
      background: k.accentSoft,
      color: k.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    fileChip: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: 16,
      borderRadius: 16,
      border: `1px solid ${k.border}`,
      background: k.subtle,
      marginTop: 16,
    },
    fileIcon: {
      width: 44,
      height: 44,
      borderRadius: 12,
      background: "rgba(16,185,129,0.14)",
      color: "#10b981",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    alert: (tone) => {
      const c = tone === "error" ? "#ef4444" : tone === "warning" ? "#f59e0b" : tone === "success" ? "#10b981" : "#3b82f6";
      return {
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "12px 14px",
        borderRadius: 12,
        border: `1px solid ${c}55`,
        background: `${c}14`,
        color: k.text,
        fontSize: 14,
        lineHeight: 1.5,
        marginTop: 16,
      };
    },

    // Step 3 — review
    statGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))",
      gap: 12,
      marginBottom: 18,
    },
    stat: (color, active) => ({
      padding: "14px 16px",
      borderRadius: 14,
      border: active ? `1.5px solid ${color}` : `1px solid ${k.border}`,
      background: active ? `${color}14` : k.subtle,
      cursor: "pointer",
      textAlign: isRTL ? "right" : "left",
      fontFamily: "inherit",
      color: k.text,
      width: "100%",
    }),
    statValue: (color) => ({ fontSize: 26, fontWeight: 800, color, lineHeight: 1.1 }),
    statLabel: { fontSize: 12, fontWeight: 700, color: k.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 4 },
    toolbar: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" },
    search: {
      flex: "1 1 220px",
      height: 40,
      borderRadius: 12,
      border: `1px solid ${k.border}`,
      background: k.dark ? "rgba(255,255,255,0.05)" : "#fff",
      color: k.text,
      padding: isRTL ? "0 38px 0 12px" : "0 12px 0 38px",
      fontSize: 14,
      outline: "none",
      width: "100%",
      boxSizing: "border-box",
      fontFamily: "inherit",
    },
    table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 14 },
    th: {
      position: "sticky",
      top: 0,
      zIndex: 1,
      background: k.surfaceSolid,
      color: k.muted,
      fontSize: 12,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      padding: "10px 12px",
      textAlign: isRTL ? "right" : "left",
      borderBottom: `1px solid ${k.border}`,
      whiteSpace: "nowrap",
    },
    td: { padding: "12px", borderBottom: `1px solid ${k.border}`, verticalAlign: "top" },
    tableWrap: {
      border: `1px solid ${k.border}`,
      borderRadius: 16,
      overflow: "auto",
      maxHeight: "min(52vh, 520px)",
    },
    pill: (color) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color,
      background: `${color}1f`,
      border: `1px solid ${color}40`,
      whiteSpace: "nowrap",
    }),
    actionBadge: (bg, border) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 700,
      background: bg,
      border: `1px solid ${border}`,
      color: k.text,
      whiteSpace: "nowrap",
    }),
    msgLine: (level) => ({
      display: "flex",
      gap: 6,
      alignItems: "flex-start",
      fontSize: 13,
      color: level === "error" ? "#ef4444" : "#d97706",
      marginTop: 4,
      lineHeight: 1.45,
    }),
    compare: {
      display: "grid",
      gridTemplateColumns: "auto 1fr auto 1fr",
      gap: "6px 10px",
      alignItems: "center",
      fontSize: 13,
    },
    detailPanel: {
      padding: 16,
      borderRadius: 14,
      background: k.subtle,
      border: `1px solid ${k.border}`,
    },
    miniTable: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    miniTh: { textAlign: isRTL ? "right" : "left", color: k.muted, fontWeight: 700, padding: "4px 8px", fontSize: 12 },
    miniTd: { padding: "6px 8px", borderTop: `1px solid ${k.border}` },

    // Confirm popover
    confirmBackdrop: {
      position: "absolute",
      inset: 0,
      background: "rgba(2,6,23,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 5,
      padding: 16,
      borderRadius: 24,
      animation: "nxbFade .2s ease",
    },
    confirmCard: {
      width: "min(440px, 100%)",
      background: k.surfaceSolid,
      borderRadius: 20,
      border: `1px solid ${k.border}`,
      boxShadow: k.shadow,
      padding: 24,
      animation: "nxbRise .25s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    confirmList: { listStyle: "none", padding: 0, margin: "14px 0 20px", display: "grid", gap: 10 },
    confirmItem: { display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: k.text },
    dot: (color) => ({ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }),

    // Step 4 — progress / results
    progressWrap: { textAlign: "center", padding: "32px 12px 8px" },
    ring: { width: 72, height: 72, margin: "0 auto 18px", color: k.accent },
    bar: { height: 10, borderRadius: 999, background: k.subtle2, overflow: "hidden", border: `1px solid ${k.border}` },
    barFill: (pct) => ({
      height: "100%",
      width: `${pct}%`,
      background: k.accentGradient,
      borderRadius: 999,
      transition: "width .6s ease",
    }),
    resultTile: (color) => ({
      padding: "16px 18px",
      borderRadius: 16,
      border: `1px solid ${color}40`,
      background: `${color}12`,
    }),

    // Floating progress card
    floatCard: {
      position: "fixed",
      bottom: 24,
      [isRTL ? "left" : "right"]: 24,
      width: "min(340px, calc(100vw - 32px))",
      background: k.surfaceSolid,
      color: k.text,
      border: `1px solid ${k.border}`,
      borderRadius: 18,
      boxShadow: k.shadow,
      padding: 16,
      zIndex: 1090,
      direction: isRTL ? "rtl" : "ltr",
      animation: "nxbRise .3s cubic-bezier(0.22, 1, 0.36, 1)",
    },

    headerButton: {
      ...btnBase,
      border: "none",
      color: "#fff",
      background: k.accentGradient,
      boxShadow: "none",
      height: 44,
      padding: "0 20px",
    },
  };
};

/** Global keyframes + interaction states (hover/focus/responsive). */
export const BULK_CSS = `
@keyframes nxbFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes nxbRise { from { opacity: 0; transform: translateY(14px) scale(.985) } to { opacity: 1; transform: none } }
@keyframes nxbSpin { to { transform: rotate(360deg) } }
.nxb-btn:not(:disabled):hover { transform: translateY(-1px); }
.nxb-btn:not(:disabled):active { transform: translateY(0); }
.nxb-btn:focus-visible, .nxb-card:focus-visible, .nxb-seg:focus-visible, .nxb-stat:focus-visible, .nxb-drop:focus-visible {
  outline: 3px solid rgba(46,124,186,.55); outline-offset: 2px;
}
.nxb-card:not(:disabled):hover { transform: translateY(-2px); }
.nxb-row:hover td { background: rgba(46,124,186,.05); }
.nxb-spin { animation: nxbSpin 1s linear infinite; }
.nxb-search:focus { border-color: #2e7cba !important; }
.nxb-show-sm { display: none; }
@media (max-width: 640px) {
  .nxb-shell { height: 100vh !important; max-height: 100vh !important; border-radius: 0 !important; }
  .nxb-overlay { padding: 0 !important; }
  .nxb-hide-sm { display: none !important; }
  .nxb-show-sm { display: block !important; }
  .nxb-body { padding: 18px 16px !important; }
  .nxb-header, .nxb-footer, .nxb-stepper { padding-left: 16px !important; padding-right: 16px !important; }
}
@media (prefers-reduced-motion: reduce) {
  .nxb-shell, .nxb-overlay, .nxb-float { animation: none !important; }
  .nxb-btn, .nxb-card { transition: none !important; }
}
`;
